import { useEffect, useRef, useState, type Ref } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-light-svg-icons";
import { useApi } from "@core/api";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { useQuery } from "@tanstack/react-query";
import { readSiteBanners, type SiteBannerSlot } from "@/overrides/site/siteBanners";
import { clearSiteTopBannerHeight, setSiteTopBannerHeight } from "./siteTopBanners";

type SiteAnnouncementBarProps = {
  label: string;
  message: string;
  onDismiss?: () => void;
  fixed?: boolean;
  bannerRef?: Ref<HTMLDivElement>;
};

export function SiteAnnouncementBar({ label, message, onDismiss, fixed, bannerRef }: SiteAnnouncementBarProps) {
  const trimmedLabel = label.trim();
  return (
    <div
      ref={bannerRef}
      data-testid={fixed ? "site-announcement-banner" : undefined}
      className={`${fixed ? "fixed left-0 right-0" : "relative"} flex items-center justify-between gap-3 px-4 py-1.5 print:hidden`}
      style={{
        top: fixed ? "var(--site-top-banner-below-account, 0px)" : undefined,
        zIndex: fixed ? 249 : undefined,
        background: "#0a1628",
        borderBottom: "1px solid #1a2e4a",
      }}
    >
      {trimmedLabel && (
        <span className="shrink-0 text-sm font-semibold tracking-widest uppercase" style={{ color: "#D4AF37", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.12em" }}>
          {trimmedLabel}
        </span>
      )}
      <span className="min-w-0 flex-1 text-sm" style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Outfit', sans-serif" }}>
        {message}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="ml-auto shrink-0 p-1"
        style={{ color: "rgba(255,255,255,0.5)" }}
        aria-label="Dismiss"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );
}

function dismissStorageKey(userId: number | undefined, audience: "private" | "standard", slot: SiteBannerSlot): string | null {
  if (!slot.message.trim() || !slot.revision) return null;
  const who = userId ? String(userId) : "guest";
  return `siteAnnouncementDismissed:${who}:${audience}:${slot.revision}`;
}

export default function SiteAnnouncementBanner() {
  const api = useApi();
  const { user, isLoading: userLoading } = useCurrentUser();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const bannersQuery = useQuery({
    queryKey: ["site-banners"],
    queryFn: () => api.api.get("/spg/site-banners").then((r) => readSiteBanners(r.data)).catch(() => readSiteBanners(null)),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const privateQuery = useQuery({
    queryKey: ["private-client", user?.Id],
    queryFn: () => api.GetSPGPrivateClient(),
    enabled: !!user?.Id,
    staleTime: 60_000,
  });

  const audienceReady = !userLoading && (!user?.Id || privateQuery.isFetched);
  const isPrivate = !!user?.Id && privateQuery.data?.IsPrivateClient === true;
  const audience = isPrivate ? "private" : "standard";
  const slot = isPrivate ? bannersQuery.data?.privateClient : bannersQuery.data?.standardClient;
  const dismissKey = dismissStorageKey(user?.Id, audience, slot ?? { label: "", message: "", revision: "" });

  useEffect(() => {
    if (!dismissKey) {
      setIsDismissed(false);
      return;
    }
    setIsDismissed(localStorage.getItem(dismissKey) === "true");
  }, [dismissKey]);

  const show = !!user?.Id && audienceReady && !!slot?.message.trim() && !!slot.revision && !isDismissed;

  useEffect(() => {
    if (!show) {
      clearSiteTopBannerHeight("announcement");
      return;
    }
    const updateOffset = () => {
      setSiteTopBannerHeight("announcement", bannerRef.current?.offsetHeight ?? 0);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener("resize", updateOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
      clearSiteTopBannerHeight("announcement");
    };
  }, [show, slot?.message, slot?.label]);

  if (!show || !slot) return null;

  return (
    <SiteAnnouncementBar
      label={slot.label}
      message={slot.message}
      fixed
      bannerRef={bannerRef}
      onDismiss={() => {
        if (dismissKey) localStorage.setItem(dismissKey, "true");
        setIsDismissed(true);
      }}
    />
  );
}
